import {
  EDR_SOLUTION_ID,
  OBSERVABILITY_DOMAIN_ID,
  OBSERVABILITY_DOMAIN_LABEL,
  SECURITY_CATEGORY_ID,
  SECURITY_CATEGORY_LABEL,
} from 'src/mindtime/constants/securitySolutions';
import { mapClientPermissionToResellerRole } from 'src/constants/constants';
import { normalizeRegistryPermissionKey } from 'src/mindtime/utils/permissionKeys';

export const PERMISSION_READ_SUFFIX = '-read' as const;
export const PERMISSION_CREATE_SUFFIX = '-create' as const;

export type PermissionSuffix = typeof PERMISSION_READ_SUFFIX | typeof PERMISSION_CREATE_SUFFIX;

export type SubscriptionComponentDef = {
  slug: string;
  suffix?: PermissionSuffix;
};

export type SubscriptionModuleDef = {
  /** Permission slug after product prefix. Defaults from nav/module id (`kebab-case` → `snake_case`). */
  slug?: string;
  suffix?: PermissionSuffix;
  components?: Record<string, string | SubscriptionComponentDef>;
};

export type SubscriptionCatalogTaxonomy = {
  categoryId: string;
  categoryLabel: string;
  domainId: string;
  domainLabel: string;
};

export type SubscriptionProductRegistryEntry = {
  id: string;
  prefix: string;
  /** BE product taxonomy: category → domain → solution. */
  catalog: SubscriptionCatalogTaxonomy;
  modules: Record<string, SubscriptionModuleDef>;
};

const navIdToSlug = (navId: string): string => navId.replace(/-/g, '_');

/** Normalize BE `solution` to registry product id (`EDR` → `edr`). */
export const normalizeSolutionSlug = (solution: string): string => {
  const trimmed = String(solution || '').trim();
  if (!trimmed) return '';
  const paren = trimmed.match(/\(([A-Za-z0-9]+)\)\s*$/);
  if (paren?.[1]) return paren[1].toLowerCase();
  return trimmed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
};

/** True when a permission identifier belongs to a subscription-gated registry product. */
export const permissionBelongsToSubscriptionRegistry = (permission: string): boolean => {
  const id = normalizeRegistryPermissionKey(permission);
  return Object.values(SUBSCRIPTION_PERMISSION_REGISTRY).some(({ prefix }) => {
    const p = prefix.toLowerCase();
    return id === p || id.startsWith(`${p}_`) || id.startsWith(`${p}-`);
  });
};

const resolveComponentDef = (
  def: string | SubscriptionComponentDef,
): { slug: string; suffix: PermissionSuffix } => {
  if (typeof def === 'string') {
    return { slug: def, suffix: PERMISSION_READ_SUFFIX };
  }
  return { slug: def.slug, suffix: def.suffix ?? PERMISSION_READ_SUFFIX };
};

export const buildPermissionIdentifier = (
  prefix: string,
  segments: string[],
  suffix: PermissionSuffix = PERMISSION_READ_SUFFIX,
): string => `${prefix}_${segments.join('_')}${suffix}`;

/**
 * Single registry for subscription-gated products, modules, and UI components.
 * Add a product, module, or component here once — nav, routes, guards, and pages derive permissions from it.
 */
export const SUBSCRIPTION_PERMISSION_REGISTRY: Record<string, SubscriptionProductRegistryEntry> = {
  edr: {
    id: EDR_SOLUTION_ID,
    prefix: 'client_edr',
    catalog: {
      categoryId: SECURITY_CATEGORY_ID,
      categoryLabel: SECURITY_CATEGORY_LABEL,
      domainId: OBSERVABILITY_DOMAIN_ID,
      domainLabel: OBSERVABILITY_DOMAIN_LABEL,
    },
    modules: {
      'security-operations-center': { slug: 'soc' },
      'global-filters': {
        slug: 'global_filters',
        components: {
          dateRange: 'date_range',
          organization: 'organization',
          search: 'search',
          endpoint: 'endpoint',
        },
      },
      'reseller-dashboard': {
        slug: 'reseller_dashboard',
        components: {
          portfolioHealth: 'portfolio_health',
          securityScore: 'security_score',
          clientsRequiringAttention: 'clients_requiring_attention',
          endpointsRequiringAction: 'endpoints_requiring_action',
          recentCriticalIncidents: 'recent_critical_incidents',
          openTasks: 'open_tasks',
          recentClientActivity: 'recent_client_activity',
        },
      },
      'sub-org-dashboard': {
        slug: 'sub_org_dashboard',
        components: {
          portfolioHealth: 'portfolio_health',
          securityScore: 'security_score',
          recommendedActions: 'recommended_actions',
          endpointsRequiringAction: 'endpoints_requiring_action',
          recentCriticalIncidents: 'recent_critical_incidents',
          mostVulnerableEndpoints: 'most_vulnerable_endpoints',
          endpointHealth: 'endpoint_health',
          recentActivity: 'recent_activity',
        },
      },
      clients: {
        /** JWT role only — not org subscription entitlements (all tiers). */
        slug: 'clients',
        components: {
          list: 'list',
          summary: 'summary',
        },
      },
      endpoints: {
        slug: 'endpoints',
        components: {
          table: 'table',
          agentOverview: 'agent_overview',
          deployAgent: { slug: 'deploy_agent', suffix: PERMISSION_CREATE_SUFFIX },
        },
      },
      'endpoint-dashboard': {
        slug: 'endpoint_dashboard',
        components: {
          portfolioHealth: 'portfolio_health',
          securityScore: 'security_score',
          criticalVulnerabilities: 'critical_vulnerabilities',
          failedCompliance: 'failed_compliance',
          securityOverview: 'security_overview',
          endpointSummary: 'endpoint_summary',
          endpointHealth: 'endpoint_health',
        },
      },
      /** Other endpoint-detail tabs reuse sidebar modules below (vulnerability-detection, …). */
      'endpoint-technical-details': {
        slug: 'endpoint_technical_details',
        components: {
          installCommands: 'install_commands',
        },
      },
      'configuration-compliance': {
        slug: 'configuration_compliance',
        components: {
          summary: 'summary',
          list: 'list',
          endpointCompliance: 'endpoint_compliance',
        },
      },
      'malware-detection': {
        slug: 'malware_detection',
        components: {
          summary: 'summary',
          categoriesChart: 'categories_chart',
          list: 'list',
          infectedEndpoints: 'infected_endpoints',
        },
      },
      'automated-response': {
        slug: 'automated_response',
        components: {
          summary: 'summary',
          list: 'list',
          affectedEndpoints: 'affected_endpoints',
        },
      },
      'file-integrity-monitoring': {
        slug: 'file_integrity_monitoring',
        components: {
          summary: 'summary',
          events: 'events',
          affectedEndpoints: 'affected_endpoints',
        },
      },
      'threat-hunting': {
        slug: 'threat_hunting',
        components: {
          summary: 'summary',
          resultsList: 'results_list',
          affectedEndpoints: 'affected_endpoints',
        },
      },
      'vulnerability-detection': {
        slug: 'vulnerabilities_detection',
        components: {
          summary: 'summary',
          list: 'list',
          vulnerableEndpoints: 'vulnerable_endpoints',
        },
      },
      'mitre-attck': {
        slug: 'mitre_attack',
        components: {
          summary: 'summary',
          techniquesList: 'techniques_list',
          topTechniques: 'top_techniques',
          affectedEndpoints: 'affected_endpoints',
        },
      },
    },
  },
};

export const getSubscriptionProductRegistry = (
  productId: string,
): SubscriptionProductRegistryEntry | undefined => SUBSCRIPTION_PERMISSION_REGISTRY[productId];

export const getSubscriptionCatalogTaxonomy = (
  solution: string,
): SubscriptionCatalogTaxonomy | undefined => {
  const productId = normalizeSolutionSlug(solution);
  return productId ? getSubscriptionProductRegistry(productId)?.catalog : undefined;
};

/** Catalogue grouping label (BE `domain`, else registry). EDR → Observability. */
export const resolveCatalogDomainLabel = (
  domain: unknown,
  solution: unknown,
): string | undefined => {
  const raw = typeof domain === 'string' ? domain.trim() : '';
  if (raw) return raw;
  return getSubscriptionCatalogTaxonomy(String(solution || ''))?.domainLabel;
};

export const modulePermission = (productId: string, moduleId: string): string => {
  const product = getSubscriptionProductRegistry(productId);
  if (!product) return '';
  const moduleDef = product.modules[moduleId];
  const slug = moduleDef?.slug ?? navIdToSlug(moduleId);
  const suffix = moduleDef?.suffix ?? PERMISSION_READ_SUFFIX;
  return buildPermissionIdentifier(product.prefix, [slug], suffix);
};

export const componentPermission = (
  productId: string,
  moduleId: string,
  componentKey: string,
): string => {
  const product = getSubscriptionProductRegistry(productId);
  if (!product) return '';
  const moduleDef = product.modules[moduleId];
  const moduleSlug = moduleDef?.slug ?? navIdToSlug(moduleId);
  const componentDef = moduleDef?.components?.[componentKey];
  const { slug: componentSlug, suffix } = componentDef
    ? resolveComponentDef(componentDef)
    : { slug: navIdToSlug(componentKey), suffix: PERMISSION_READ_SUFFIX };
  return buildPermissionIdentifier(product.prefix, [moduleSlug, componentSlug], suffix);
};

/** Reseller JWT role id for a module (`reseller_edr_*` mirror of `modulePermission`). */
export const moduleResellerRolePermission = (productId: string, moduleId: string): string =>
  mapClientPermissionToResellerRole(modulePermission(productId, moduleId));

/** Reseller JWT role id for a module component. */
export const componentResellerRolePermission = (
  productId: string,
  moduleId: string,
  componentKey: string,
): string =>
  mapClientPermissionToResellerRole(componentPermission(productId, moduleId, componentKey));

/** Resolve nav/route permission from explicit value or product + module id. */
export const resolveSubscriptionModulePermission = (
  productId: string | undefined,
  moduleId: string,
  explicitPermission?: string,
): string | undefined => {
  if (explicitPermission) return explicitPermission;
  if (!productId) return undefined;
  return modulePermission(productId, moduleId);
};

/** Resolve parent module `-read` permission when `permission` is a module component (e.g. deploy_agent-create). */
export const resolveRegistryParentModulePermission = (permission: string): string | undefined => {
  const normalized = normalizeRegistryPermissionKey(permission);

  for (const product of Object.values(SUBSCRIPTION_PERMISSION_REGISTRY)) {
    for (const [moduleId, moduleDef] of Object.entries(product.modules)) {
      const modulePerm = modulePermission(product.id, moduleId).toLowerCase();
      if (normalized === modulePerm) return undefined;

      const moduleSlug = (moduleDef.slug ?? navIdToSlug(moduleId)).toLowerCase();
      const modulePrefix = `${product.prefix.toLowerCase()}_${moduleSlug}`;
      if (normalized.startsWith(`${modulePrefix}_`)) {
        return modulePermission(product.id, moduleId);
      }
    }
  }

  return undefined;
};

/**
 * True when the registry module for this module `-read` key declares widget components.
 * Leaf modules (e.g. SOC) have no components — module permission alone is enough.
 */
export const registryModuleDefinesComponents = (modulePermissionKey: string): boolean => {
  const normalized = normalizeRegistryPermissionKey(modulePermissionKey);

  for (const product of Object.values(SUBSCRIPTION_PERMISSION_REGISTRY)) {
    for (const moduleId of Object.keys(product.modules)) {
      if (normalizeRegistryPermissionKey(modulePermission(product.id, moduleId)) !== normalized) {
        continue;
      }
      return Object.keys(product.modules[moduleId]?.components ?? {}).length > 0;
    }
  }

  return false;
};

/** Identifiers declared in the registry (fallback when catalog is empty). */
export const collectRegistryPermissionIdentifiers = (productId?: string): string[] => {
  const ids: string[] = [];
  const products = productId
    ? [getSubscriptionProductRegistry(productId)].filter(Boolean)
    : Object.values(SUBSCRIPTION_PERMISSION_REGISTRY);

  products.forEach((product) => {
    if (!product) return;
    Object.entries(product.modules).forEach(([moduleId, moduleDef]) => {
      // EDR Clients is JWT-role only — omit from subscription catalog identifiers.
      if (moduleId === 'clients') return;

      const moduleSlug = moduleDef.slug ?? navIdToSlug(moduleId);
      ids.push(
        buildPermissionIdentifier(
          product.prefix,
          [moduleSlug],
          moduleDef.suffix ?? PERMISSION_READ_SUFFIX,
        ),
      );
      Object.values(moduleDef.components ?? {}).forEach((def) => {
        const { slug, suffix } = resolveComponentDef(def);
        ids.push(buildPermissionIdentifier(product.prefix, [moduleSlug, slug], suffix));
      });
    });
  });

  return ids;
};

/** All registry identifiers assignable via org subscription (excludes role-only EDR Clients). */
export const KNOWN_SUBSCRIPTION_IDENTIFIERS: readonly string[] =
  collectRegistryPermissionIdentifiers();

/** Reseller JWT role ids for subscription catalog + role-only EDR Clients. */
export const KNOWN_RESELLER_ROLE_IDENTIFIERS: readonly string[] = [
  ...KNOWN_SUBSCRIPTION_IDENTIFIERS.map(mapClientPermissionToResellerRole),
  mapClientPermissionToResellerRole('client_edr_clients-read'),
  mapClientPermissionToResellerRole('client_edr_clients_list-read'),
  mapClientPermissionToResellerRole('client_edr_clients_summary-read'),
];
